import { useMemo, useState } from "react";
import {
  applyAssociationImportPreview,
  buildAssociationImportPreview,
  getDefaultWorkbookSheet,
  getWorkbookSheetNames,
  loadAllClientesSocios,
  readWorkbookFromFile,
} from "../../services/associadosImport";

function ImportacaoDescontos({ onImportFinished }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [workbook, setWorkbook] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [feedback, setFeedback] = useState("");

  const sheetNames = useMemo(
    () => (workbook ? getWorkbookSheetNames(workbook) : []),
    [workbook],
  );

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    setFeedback("");
    setPreview(null);

    if (!file) {
      setSelectedFile(null);
      setWorkbook(null);
      setSelectedSheet("");
      return;
    }

    try {
      const nextWorkbook = await readWorkbookFromFile(file);
      const defaultSheet = getDefaultWorkbookSheet(nextWorkbook);

      setSelectedFile(file);
      setWorkbook(nextWorkbook);
      setSelectedSheet(defaultSheet);
    } catch (error) {
      setSelectedFile(null);
      setWorkbook(null);
      setSelectedSheet("");
      setFeedback(
        error?.message || "Nao foi possivel ler o arquivo selecionado.",
      );
    }
  };

  const handlePreview = async () => {
    if (!workbook || !selectedSheet) {
      setFeedback("Selecione um arquivo e uma aba da planilha.");
      return;
    }

    setLoadingPreview(true);
    setFeedback("");

    try {
      const { clientes, socios } = await loadAllClientesSocios();
      const nextPreview = buildAssociationImportPreview({
        workbook,
        sheetName: selectedSheet,
        clientes,
        socios,
      });

      setPreview(nextPreview);
      setFeedback("Planilha analisada com sucesso.");
    } catch (error) {
      setPreview(null);
      setFeedback(
        error?.message || "Nao foi possivel preparar a importacao.",
      );
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleApply = async () => {
    if (!preview) {
      setFeedback("Analise a planilha antes de aplicar a importacao.");
      return;
    }

    const confirmed = window.confirm(
      "Confirma a aplicacao das alteracoes e desativacoes desta planilha?",
    );

    if (!confirmed) {
      return;
    }

    setApplying(true);
    setFeedback("");

    try {
      const result = await applyAssociationImportPreview(preview);
      const errorCount = result.errors.length;

      setFeedback(
        errorCount
          ? `Importacao concluida com ${errorCount} erro(s).`
          : "Importacao concluida com sucesso.",
      );

      if (typeof onImportFinished === "function") {
        onImportFinished();
      }
    } catch (error) {
      setFeedback(error?.message || "Falha ao aplicar a importacao.");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="card border-0 bg-light mb-4">
      <div className="card-body">
        <div className="row g-3 align-items-end">
          <div className="col-xl-4 col-md-12">
            <label className="form-label">Planilha mensal</label>
            <input
              type="file"
              className="form-control"
              accept=".xls,.xlsx"
              onChange={handleFileChange}
            />
          </div>

          <div className="col-xl-3 col-md-6">
            <label className="form-label">Competencia / aba</label>
            <select
              className="form-control"
              value={selectedSheet}
              onChange={(event) => setSelectedSheet(event.target.value)}
              disabled={!sheetNames.length}
            >
              <option value=""></option>
              {sheetNames.map((sheetName) => (
                <option key={sheetName} value={sheetName}>
                  {sheetName}
                </option>
              ))}
            </select>
          </div>

          <div className="col-xl-5 col-md-6 d-flex gap-2">
            <button
              className="btn btn-outline-primary"
              onClick={handlePreview}
              disabled={!selectedFile || loadingPreview || applying}
            >
              {loadingPreview ? "Lendo planilha..." : "Analisar planilha"}
            </button>
            <button
              className="btn btn-success"
              onClick={handleApply}
              disabled={!preview || loadingPreview || applying}
            >
              {applying ? "Aplicando..." : "Aplicar importacao"}
            </button>
          </div>
        </div>

        {feedback ? <p className="mt-3 mb-0 text-secondary">{feedback}</p> : null}

        {preview ? (
          <div className="mt-4">
            <div className="row g-3">
              <div className="col-xl-2 col-md-4">
                <div className="border rounded-3 bg-white p-3">
                  <small className="text-secondary d-block">Linhas</small>
                  <strong>{preview.summary.totalRows}</strong>
                </div>
              </div>
              <div className="col-xl-2 col-md-4">
                <div className="border rounded-3 bg-white p-3">
                  <small className="text-secondary d-block">Identificados</small>
                  <strong>{preview.summary.matched}</strong>
                </div>
              </div>
              <div className="col-xl-2 col-md-4">
                <div className="border rounded-3 bg-white p-3">
                  <small className="text-secondary d-block">Pendentes</small>
                  <strong>{preview.summary.unresolved}</strong>
                </div>
              </div>
              <div className="col-xl-2 col-md-4">
                <div className="border rounded-3 bg-white p-3">
                  <small className="text-secondary d-block">Atualizacoes</small>
                  <strong>
                    {preview.summary.clientUpdates + preview.summary.socioUpdates}
                  </strong>
                </div>
              </div>
              <div className="col-xl-2 col-md-4">
                <div className="border rounded-3 bg-white p-3">
                  <small className="text-secondary d-block">Novos vinculos</small>
                  <strong>{preview.summary.socioCreates}</strong>
                </div>
              </div>
              <div className="col-xl-2 col-md-4">
                <div className="border rounded-3 bg-white p-3">
                  <small className="text-secondary d-block">Desativacoes</small>
                  <strong>{preview.summary.deactivations}</strong>
                </div>
              </div>
            </div>

            {preview.previewItems.some((item) => item.status !== "matched") ? (
              <div className="mt-4">
                <h6>Linhas nao conciliadas</h6>
                <div className="table-responsive">
                  <table className="table table-sm table-striped">
                    <thead>
                      <tr>
                        <th>Linha</th>
                        <th>Nome</th>
                        <th>SIAPE</th>
                        <th>CPF</th>
                        <th>Orgao</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.previewItems
                        .filter((item) => item.status !== "matched")
                        .slice(0, 20)
                        .map((item) => (
                          <tr key={`${item.rowNumber}-${item.nome}`}>
                            <td>{item.rowNumber}</td>
                            <td>{item.nome}</td>
                            <td>{item.siape}</td>
                            <td>{item.cpf}</td>
                            <td>{item.orgao}</td>
                            <td>{item.matchedBy || item.status}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ImportacaoDescontos;
